"use client";

import Script from "next/script";
import { useEffect, useState } from "react";

const GA_MEASUREMENT_ID = "G-BZV9QVJYV4";
const CONSENT_KEY = "marginglow_privacy_consent_v1";

type ConsentChoice = {
  analytics: boolean;
  hubspot: boolean;
  decidedAt: string;
  version: "1";
};

const REGULATED_COUNTRY_CODES = new Set([
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LI",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
  "CH",
  "GB",
]);

const REGULATED_TIMEZONES = [
  "Europe/",
  "Atlantic/Reykjavik",
  "Atlantic/Canary",
  "Atlantic/Faroe",
  "Atlantic/Madeira",
];

function visitorNeedsConsent(): boolean {
  if (typeof window === "undefined") {
    return true;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";

  if (
    REGULATED_TIMEZONES.some(
      (prefix) => timezone === prefix || timezone.startsWith(prefix),
    )
  ) {
    return true;
  }

  const locales = [navigator.language, ...(navigator.languages || [])].filter(
    Boolean,
  );

  return locales.some((locale) => {
    const countryCode = locale.split("-")[1]?.toUpperCase();
    return countryCode ? REGULATED_COUNTRY_CODES.has(countryCode) : false;
  });
}

function readSavedConsent(): ConsentChoice | null {
  try {
    const saved = window.localStorage.getItem(CONSENT_KEY);

    if (!saved) {
      return null;
    }

    const parsed = JSON.parse(saved);

    if (
      parsed?.version !== "1" ||
      typeof parsed.analytics !== "boolean"
    ) {
      return null;
    }

    return { ...parsed, hubspot: false } as ConsentChoice;
  } catch {
    return null;
  }
}

function saveConsent(choice: ConsentChoice) {
  window.localStorage.setItem(CONSENT_KEY, JSON.stringify(choice));
}

export default function PrivacyConsent() {
  const [ready, setReady] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  useEffect(() => {
    const regulated = visitorNeedsConsent();
    const saved = readSavedConsent();

    setNeedsConsent(regulated);

    if (saved) {
      setAnalytics(saved.analytics);
    } else if (regulated) {
      setShowBanner(true);
    } else {
      setAnalytics(true);
    }

    setReady(true);

    function openPrivacySettings() {
      setShowBanner(false);
      setShowSettings(true);
    }

    window.addEventListener(
      "marginglow-open-privacy-settings",
      openPrivacySettings,
    );

    return () => {
      window.removeEventListener(
        "marginglow-open-privacy-settings",
        openPrivacySettings,
      );
    };
  }, []);

  function applyConsent(analyticsAllowed: boolean) {
    const choice: ConsentChoice = {
      analytics: analyticsAllowed,
      hubspot: false,
      decidedAt: new Date().toISOString(),
      version: "1",
    };

    saveConsent(choice);
    setAnalytics(analyticsAllowed);
    setShowBanner(false);
    setShowSettings(false);
  }

  if (!ready) {
    return null;
  }

  return (
    <>
      {analytics && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            strategy="lazyOnload"
          />

          <Script id="google-analytics" strategy="lazyOnload">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_MEASUREMENT_ID}', {
                anonymize_ip: true
              });
            `}
          </Script>
        </>
      )}

      {showBanner && needsConsent && (
        <div
          className="fixed inset-x-0 bottom-0 z-[200] px-4 pb-4 sm:px-6 sm:pb-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-banner-title"
        >
          <div className="mx-auto max-w-5xl rounded-[2rem] border border-[#b9ddd6] bg-white p-6 shadow-[0_24px_80px_rgba(9,39,35,0.28)] md:p-7">
            <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <p className="text-xs font-light uppercase tracking-[0.2em] text-[#007c7a]">
                  Privacy, without the creepy stuff
                </p>

                <h2
                  id="privacy-banner-title"
                  className="mt-2 text-2xl font-light text-[#092723]"
                >
                  Glow respects your privacy.
                </h2>

                <p className="mt-3 max-w-3xl text-sm font-light leading-6 text-[#52635f]">
                  We use essential technology to keep MarginGlow working. With
                  your permission, Google Analytics helps us
                  understand what is useful and improve the experience. We do
                  not sell your personal data.
                </p>

                <div className="mt-3 flex flex-wrap gap-4 text-sm font-light">
                  <a href="/privacy" className="text-[#007c7a]">
                    Privacy Policy
                  </a>

                  <a href="/gdpr" className="text-[#007c7a]">
                    Your data rights
                  </a>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                <button
                  type="button"
                  onClick={() => applyConsent(true)}
                  className="rounded-2xl bg-[#007c7a] px-6 py-3 text-sm font-normal text-white transition hover:bg-[#006b69]"
                >
                  Accept optional cookies
                </button>

                <button
                  type="button"
                  onClick={() => applyConsent(false)}
                  className="rounded-2xl border border-[#b9d5cf] bg-white px-6 py-3 text-sm font-light text-[#092723] transition hover:bg-[#f4fbf8]"
                >
                  Essential only
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowBanner(false);
                    setShowSettings(true);
                  }}
                  className="px-4 py-2 text-sm font-light text-[#007c7a]"
                >
                  Choose settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSettings && (
        <div
          className="fixed inset-0 z-[210] flex items-center justify-center bg-[#092723]/70 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-settings-title"
        >
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-white p-6 shadow-2xl md:p-8">
            <p className="text-xs font-light uppercase tracking-[0.2em] text-[#007c7a]">
              Privacy settings
            </p>

            <h2
              id="privacy-settings-title"
              className="mt-3 text-3xl font-light text-[#092723]"
            >
              Choose what Glow may use.
            </h2>

            <div className="mt-7 space-y-4">
              <div className="rounded-3xl border border-[#d7e5df] bg-[#f4fbf8] p-5">
                <div className="flex items-start justify-between gap-5">
                  <div>
                    <h3 className="text-lg font-light text-[#092723]">
                      Essential technology
                    </h3>

                    <p className="mt-2 text-sm font-light leading-6 text-[#52635f]">
                      Required for sign-in, security, saved preferences and core
                      MarginGlow functions.
                    </p>
                  </div>

                  <span className="rounded-full bg-white px-3 py-2 text-xs font-normal text-[#007c7a]">
                    Always active
                  </span>
                </div>
              </div>

              <label className="flex cursor-pointer items-start justify-between gap-5 rounded-3xl border border-[#d7e5df] p-5">
                <div>
                  <h3 className="text-lg font-light text-[#092723]">
                    Google Analytics
                  </h3>

                  <p className="mt-2 text-sm font-light leading-6 text-[#52635f]">
                    Helps us understand which pages and features are useful,
                    using aggregated website and product usage information.
                  </p>
                </div>

                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(event) => setAnalytics(event.target.checked)}
                  className="mt-1 h-5 w-5 accent-[#007c7a]"
                />
              </label>


            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => applyConsent(analytics)}
                className="rounded-2xl bg-[#007c7a] px-6 py-3 text-sm font-normal text-white"
              >
                Save my choices
              </button>

              <button
                type="button"
                onClick={() => applyConsent(false)}
                className="rounded-2xl border border-[#b9d5cf] px-6 py-3 text-sm font-light text-[#092723]"
              >
                Use essential only
              </button>

              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="px-5 py-3 text-sm font-light text-[#007c7a]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
